import React, {useRef, useState, useCallback, useEffect} from 'react';
import { connect } from "react-redux";

const TOTAL_WIDTH = 40;
const DOUBLE_CLICK = 250;

function ParameterBox({
    isDraggable=true,
    track,
    color,
    name,
    rackTrack,
    value,
    parameter,
    id,
    min=parameter.min,
    max=parameter.max,
    width=TOTAL_WIDTH,
    units="",
    display,
    root=2,
    stopPropagation=true
}) {
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [editing, setEditing] = useState(false);
    const [textValue, setTextValue] = useState('');
    const lastClick = useRef(0);
    const inputRef = useRef(undefined);
    let style = {
        width: getWidth(value, min, max, width, root),
        backgroundColor: color
    };

    const onKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            let x = parseFloat(textValue);
            if (isNaN(x)) {
                return;
            }
            parameter.set(x, true);
            setEditing(false);
        }
    }, [setEditing, textValue, parameter]);

    useEffect(() => {
        if (editing) {
            setTextValue(Math.round(100*parameter.get(true))/100);
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.select();
            }
        }
    }, [editing, setTextValue, parameter]);

    const getId = () => {
        return parameter.id;
    };

    const onMouseDown = useCallback((e) => {
        e.stopPropagation();
        let now = new Date().getTime();
        if (now - lastClick.current < DOUBLE_CLICK) {
            setEditing(true);
            return;
        }
        lastClick.current = now;
        if (stopPropagation || !isDraggable) {
            e.stopPropagation();
        }
        setIsMouseDown(true);
    }, [isDraggable, parameter, setIsMouseDown]);

    const onMouseMove = useCallback((e) => {
        if (stopPropagation) {
            e.stopPropagation();
        }
        if (!isMouseDown) {
            return;
        }
        let newVolume = getVolumeFromEvent(e, getId(), min, max, width, root);
        if (root === 1) {
            newVolume = Math.round(newVolume);
        }
        parameter.set(newVolume, false);
    }, [parameter, isMouseDown]);

    const round = (x) => {
        return Math.round(x*100) / 100;
    };

    if (editing) {
        return (
            <div className="p-box">
              <div className="p-name">
                {name}
              </div>
              <input
                ref={inputRef}
                className="volume-box"
                onChange={(e) => setTextValue(e.target.value)}
                onKeyDown={onKeyDown}
                value={textValue}
              />
            </div>);
    }
    return (
        <div className="p-box">
          <div className="p-name">
            {name}
          </div>
          <div
            draggable={true}
            onDragStart={(e) => e.preventDefault()}
            style={{width: width + 6}}
            id={getId()}
            onClick={(e) => e.stopPropagation()}
            onMouseUp={(e) => {
                e.stopPropagation();
                setIsMouseDown(false);
            }}
            onMouseLeave={(e) => {
                e.stopPropagation();
                setIsMouseDown(false);
            }}
            onMouseMove={onMouseMove}
            onMouseDown={onMouseDown}
            className="volume-box">
            <div
              style={style}
              className="volume-indicator">
            </div>
            <span className="volume-value">
            {display ? round(display(value)) : round(value)} {units}
            </span>
          </div>
        </div>
    );
}

function getVolumeFromEvent(e, id, min, max, width, root) {
    let rect = e.target.getBoundingClientRect();
    let x = e.clientX - rect.left;
    let vol = getVolumeFromX(x, min, max, width, root);
    if (isNaN(vol)) {
        return -100;
    }
    return vol;
}

function getWidth(volume, min=-40, max=4, width, root) {
    let ratio = getVolumeRatio(volume, min, max, width);
    return width * Math.pow(ratio, 1.0/root) + 4;
}

function getVolumeFromX(x, min=-40, max=4, width, root) {
    x = Math.min(Math.max(0, x), width);
    let ratio = x / width;
    let fullRatio = Math.pow(ratio, root);
    return min + (max - min) * fullRatio;
}

function getVolumeRatio(volume, min=-40, max=4) {
    return (volume - min) / (max - min);
}

const mapStateToProps = (state, props) => {
    return {
        value: props.parameter.get()
    };
};
 

export default connect(mapStateToProps, null)(ParameterBox);
